import { useState, useEffect } from "react";
import ModalBox from "../../components/ModalBox/ModalBox";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import ActivityHeader from "../../components/ActivityHeader/ActivityHeader";
import { startModal, endModal } from "./Modals";
import "./MathBlitz.css";
import { trackGameStart } from "../../utils/trackEvents";

export default function MathBlitz() {
  const [qs, setQs] = useState([]);
  const [timer, setTimer] = useState(90);
  const [startOpen, setStartOpen] = useState(true);
  const [endOpen, setEndOpen] = useState(false);
  const [finished, setFinished] = useState(false);

  const generateQs = () => {
    const rand = (min, max) =>
      Math.floor(Math.random() * (max - min + 1)) + min;
    return ["add", "sub", "mult", "div"].flatMap((type) =>
      Array.from({ length: 5 }).map((_, i) => {
        let a, b, ans;
        if (type === "div") {
          b = rand(1, 9);
          const q = rand(Math.ceil(10 / b), Math.floor(99 / b));
          a = q * b;
          ans = q;
        } else {
          a = rand(10, 99);
          b = type === "mult" ? rand(1, 9) : rand(10, 99);
          ans = type === "add" ? a + b : type === "sub" ? a - b : a * b;
        }
        return { id: `${type}-${i}`, type, a, b, ans, ua: "", rev: false };
      })
    );
  };

  useEffect(() => {
    setQs(generateQs());
  }, []);

  useEffect(() => {
    if (!startOpen && !endOpen && !finished && timer > 0) {
      const t = setTimeout(() => setTimer((t) => t - 1), 1000);
      return () => clearTimeout(t);
    }
    if (!startOpen && !finished && timer === 0) {
      setFinished(true);
      setEndOpen(true);
    }
  }, [timer, startOpen, endOpen, finished, qs]);

  const allFilled = qs.every((q) => q.ua !== "");
  const handleSubmit = () => {
    if (!allFilled) return;
    setFinished(true);
    setEndOpen(true);
  };

  const handleRetry = () => {
    setQs(generateQs());
    setTimer(90);
    setFinished(false);
    setEndOpen(false);
  };

  const correct = qs.filter(
    (q) => q.ua !== "" && Number(q.ua) === q.ans
  ).length;
  const wrong = qs.filter((q) => q.ua !== "").length - correct;
  const getPoints = () => correct * 10 + wrong * -2 + timer;

  const viewAnswers = () => {
    setEndOpen(false);
    setQs(qs.map((q) => ({ ...q, rev: true })));
  };

  const renderInput = (q, symbol) => (
    <div key={q.id} className="math-blitz-input">
      {q.a} {symbol} {q.b} <span>=</span>
      <input
        type="number"
        value={q.ua}
        disabled={q.rev}
        onChange={(e) =>
          setQs((s) =>
            s.map((x) => (x.id === q.id ? { ...x, ua: e.target.value } : x))
          )
        }
      />
      {q.rev && (
        <span className={Number(q.ua) === q.ans ? "correct" : "wrong"}>
          {q.ans}
        </span>
      )}
    </div>
  );

  return (
    <div className="math-blitz-main">
      <ModalBox
        isOpen={startOpen}
        {...startModal()}
        onClose={() => {
          setStartOpen(false);
          trackGameStart("MathBlitz", 1);
        }}
      />

      <ActivityHeader>
        Time: {timer}s
        <PrimaryButton
          onClick={finished ? handleRetry : handleSubmit}
          disabled={!finished && !allFilled}
          type={finished ? "retry" : "start"}
          title={
            !finished && !allFilled
              ? "Answer all questions to enable Submit"
              : ""
          }
        >
          {finished ? "Retry" : "Submit"}
        </PrimaryButton>
      </ActivityHeader>

      <div className="math-blitz-content">
        <div className="math-blitz-add">
          {qs.filter((q) => q.type === "add").map((q) => renderInput(q, "+"))}
        </div>
        <div className="math-blitz-sub">
          {qs.filter((q) => q.type === "sub").map((q) => renderInput(q, "−"))}
        </div>
        <div className="math-blitz-mult">
          {qs.filter((q) => q.type === "mult").map((q) => renderInput(q, "×"))}
        </div>
        <div className="math-blitz-div">
          {qs.filter((q) => q.type === "div").map((q) => renderInput(q, "÷"))}
        </div>
      </div>

      <ModalBox
        isOpen={endOpen}
        {...endModal(correct, wrong, timer, getPoints())}
        onClose={viewAnswers}
      />
    </div>
  );
}
